import React, { useEffect, useState } from 'react';
import { Wallet, X as XIcon, RefreshCw } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import brokerService from '../../services/brokerService';
import type { BrokerAccount } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accounts: BrokerAccount[];
  /** Called after every account is saved successfully. */
  onSaved: () => void;
}

/**
 * Self-service "Set trading capital" modal — the WL-friendly counterpart to
 * the full EditBrokerAccountForm. Shows only the capital field per account.
 *
 * Used in two places:
 *  - Inline prompt on Analytics Overview when capital is missing / zero
 *  - Account page (user-menu dropdown) for permanent self-service
 *
 * PUT /broker-accounts/{client_id} with `{ capital }` is Cognito-protected and
 * caller-scoped, so WL regular users can call it for their own accounts
 * without needing the full Brokers admin surface.
 */
const SetCapitalDialog: React.FC<Props> = ({ isOpen, onClose, accounts, onSaved }) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset values whenever the dialog opens with a fresh account list.
  useEffect(() => {
    if (!isOpen) return;
    const next: Record<string, string> = {};
    for (const a of accounts) {
      next[a.client_id] = a.capital ? String(a.capital) : '';
    }
    setValues(next);
    setError(null);
  }, [isOpen, accounts]);

  const handleChange = (clientId: string, raw: string) => {
    // Allow digits only (no decimals — capital is whole rupees in this UI).
    const cleaned = raw.replace(/[^0-9]/g, '');
    setValues((v) => ({ ...v, [clientId]: cleaned }));
  };

  const handleSave = async () => {
    setError(null);
    // Walk each account and bucket its current input:
    //   - empty            → user didn't intend to touch this account; ignore
    //   - invalid (≤ 0)    → block with a clear error
    //   - same as current  → no-op, ignore
    //   - new positive int → queue for update
    const updates: Array<{ clientId: string; capital: number }> = [];
    let invalidFound = false;

    for (const a of accounts) {
      const raw = (values[a.client_id] ?? '').trim();
      if (!raw) continue;
      const num = Number.parseInt(raw, 10);
      if (!Number.isFinite(num) || num <= 0) {
        invalidFound = true;
        continue;
      }
      if (num === (a.capital ?? 0)) continue;
      updates.push({ clientId: a.client_id, capital: num });
    }

    if (invalidFound) {
      setError('Capital must be a positive number.');
      return;
    }
    if (updates.length === 0) {
      // No changes — close silently. Clicking Save with the pre-filled value
      // unchanged is a perfectly reasonable thing to do; don't penalise it.
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        updates.map((u) =>
          brokerService.updateBrokerAccount(u.clientId, { capital: u.capital }),
        ),
      );
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save capital. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" ariaLabel="Set trading capital">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Set trading capital
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Capital is the denominator for Total Return %, CAGR and Sharpe ratio
            on your Analytics page. Enter the ₹ amount you've allocated for
            algo trading on this account.
          </p>

          <div className="space-y-3">
            {accounts.map((a) => (
              <div key={a.client_id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {a.broker_name}
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{a.client_id}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Current: ₹{(a.capital ?? 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">₹</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 100000"
                    value={values[a.client_id] ?? ''}
                    onChange={(e) => handleChange(a.client_id, e.target.value)}
                    disabled={saving}
                    className="w-44 pl-7 pr-3 py-2 text-sm rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 tabular-nums text-right"
                  />
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            leftIcon={saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save capital'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SetCapitalDialog;
